import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import { getBalanceDetailsAPI, BalanceDetailResponse } from '../../../services/adminApi';
import { useModulePermission } from '../../../hooks/useModulePermission';
import styles from './BalanceDetailModal.module.css';

interface BalanceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  refId: number;
  refType: string; // 'Token', 'INR', 'Dollar'
  title: string;
  displayName: string;
}

const BalanceDetailModal: React.FC<BalanceDetailModalProps> = ({
  isOpen,
  onClose,
  refId,
  refType,
  displayName,
}) => {
  const [balanceDetails, setBalanceDetails] = useState<BalanceDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const permissions = useModulePermission('Reports');

  useEffect(() => {
    if (isOpen) {
      fetchBalanceDetails();
    }
  }, [isOpen]);

  const fetchBalanceDetails = async () => {
    try {
      setLoading(true);
      const request: any = {
        refId: refId,
        refType: refType
      };

      const response = await getBalanceDetailsAPI(request);

      if (response.isSuccess && response.data) {
        setBalanceDetails(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch balance details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!balanceDetails || !balanceDetails.transactions) return;

    // Prepare CSV data
    const headers = ['Date', 'Transaction Type', 'Party', 'Quantity', 'Balance'];
    const rows = balanceDetails.transactions.map(txn => [
      txn.transactionDate ? new Date(txn.transactionDate).toLocaleDateString('en-IN') : '-',
      txn.transactionType || '-',
      txn.partyName || '-',
      `${txn.transactionType === 'Buy' ? '+' : '-'}${formatBalance(txn.qty)}`,
      `${getCurrencySymbol()}${formatBalance(txn.balance)}`
    ]);

    // Create CSV content
    const csvContent = [
      [
        `Balance Detail: ${balanceDetails.tokenName || balanceDetails.accountName || displayName}`,
        '',
        '',
        '',
        ''
      ],
      [
        `Opening: ${getCurrencySymbol()}${formatBalance(balanceDetails.openingBalance)}`,
        `Closing: ${getCurrencySymbol()}${formatBalance(balanceDetails.closingBalance)}`,
        `Entries: ${balanceDetails.totalEntries || 0}`,
        '',
        ''
      ],
      [],
      headers,
      ...rows
    ]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    // Download CSV
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
    element.setAttribute('download', `balance-detail-${displayName}-${new Date().getTime()}.csv`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const getCurrencySymbol = () => {
    switch (refType) {
      case 'Dollar':
        return '$';
      case 'INR':
        return '₹';
      default:
        return '';
    }
  };

  const formatBalance = (value: number | undefined) => {
    if (value === undefined || value === null) return '0.00';
    
    switch (refType) {
      case 'Token':
        return value.toFixed(4);
      case 'INR':
      case 'Dollar':
        return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      default:
        return value.toFixed(2);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Balance Detail: ${balanceDetails?.tokenName || balanceDetails?.accountName || displayName}`}
      headerAction={
        permissions.canExport && (
          <button className={styles.exportBtn} onClick={handleExport} title="Export">
            📥 Export
          </button>
        )
      }
    >
      <div className={styles.balanceDetailContainer}>
        {/* Header Stats */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>OPENING</div>
            <div className={styles.statValue}>
              {getCurrencySymbol()}{formatBalance(balanceDetails?.openingBalance)}
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>CLOSING</div>
            <div className={styles.statValue}>
              {getCurrencySymbol()}{formatBalance(balanceDetails?.closingBalance)}
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>ENTRIES</div>
            <div className={styles.statValue}>{balanceDetails?.totalEntries || 0}</div>
          </div>
        </div>

        {/* Transactions List */}
        <div className={styles.transactionsContainer}>
          {loading ? (
            <div className={styles.loading}>Loading balance details...</div>
          ) : !balanceDetails || !balanceDetails.transactions || balanceDetails.transactions.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No transactions found</p>
            </div>
          ) : (
            <div className={styles.transactionsList}>
              {balanceDetails.transactions.map((txn, index) => (
                <div key={index} className={styles.transactionRow}>
                  <div className={styles.dateAndPartyCol}>
                    <div className={styles.dateAndBadge}>
                      <div className={styles.txnDate}>
                        {txn.transactionDate ? new Date(txn.transactionDate).toLocaleDateString('en-IN') : '-'}
                      </div>
                      <span className={`${styles.badge} ${txn.transactionType === 'Buy' ? styles.buyBadge : txn.transactionType === 'Sell' ? styles.sellBadge : styles.swapBadge}`}>
                        {txn.transactionType || '-'}
                      </span>
                    </div>
                    <div className={styles.partyName}>
                      {txn.partyName || '-'}
                    </div>
                  </div>
                  <div className={styles.qtyCol}>
                    <span className={(txn.qty ?? 0) > 0 ? styles.green : styles.red}>
                      {formatBalance(txn.qty)}
                    </span>
                  </div>
                  <div className={styles.balanceCol}>
                    {getCurrencySymbol()}{formatBalance(txn.balance)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BalanceDetailModal;
